import { useEffect, useRef, useState } from "react";
import ReactDatePicker from "react-datepicker";
import { format, isValid, parse } from "date-fns";
// import { PropsValue } from "react-select";

import DatePicker from "./DatePicker";
import Input from "./Input";
import Select from "./Select";
import SelectWithSearch, { SelectOption } from "./SelectWithSearch";
import Tabs from "./Tabs";
import { FilterInput, FilterValue, FilterValues } from "src/types/report";
import { END_DATE, START_DATE } from "src/constants/filter";
import { isDemoUserEmail } from "src/constants/demoUsers";
import {
  getInitialValues,
  isAllFilterOptionValue,
  normalizeMultiSelectFilters,
  toMultiFilterValue,
  maskedProductLabels,
} from "src/helpers/Filter";
import { useAuthStore } from "src/store/authStore";

type ReportFiltersProps = {
  inputFields?: FilterInput[];
  defaultValues?: FilterValues;
  onSubmit?: (filters: { fields: FilterValues }) => void;
  onClear?: () => void;
  autoSubmitOnTabChange?: boolean;
  isLoading?: boolean;
  showLabels?: boolean;
  // Filter keys rendered as a multi-select, whose value is submitted as an
  // array. Opt-in per caller rather than global: only the DASHBOARD backend
  // reads a list of products — see MULTI_SELECT_FILTER_KEYS in
  // src/helpers/Filter.ts, which is what DashboardPage passes here. ReportPage
  // passes nothing and keeps every filter single-valued.
  multiSelectKeys?: readonly string[];
  // Fired after a successful Apply. Named for the event rather than the
  // effect: the page decides whether that should close the panel, which it
  // only does on mobile.
  onFiltersApplied?: () => void;
};

// type StaticSelectOption = {
//   label: string;
//   value: string | number;
// };

const toValidDate = (
  value: FilterValue | undefined,
  fallback: string,
): Date | null => {
  const parsed = parse(String(value ?? fallback), "yyyy-MM-dd", new Date());
  return isValid(parsed) ? parsed : null;
};

// Tabs, Input and the date pickers are single-value controls, and dates are
// plain yyyy-MM-dd strings. FilterValues permits an array under any key, so
// these narrow instead of casting — only a multi-select key ever holds one, and
// none of these controls is used for those.
const scalarValue = (
  value: FilterValue | undefined,
): string | number | undefined => (Array.isArray(value) ? undefined : value);

const dateText = (value: FilterValue | undefined): string =>
  typeof value === "string" ? value : "";

// A module constant, not a `= []` default in the signature: the effect below
// lists multiSelectKeys as a dependency, and a fresh array literal per render
// would re-run it forever.
const NO_MULTI_SELECT_KEYS: readonly string[] = [];

export default function FilterBar({
  inputFields = [],
  defaultValues = {},
  onClear,
  onSubmit,
  showLabels = true,
  autoSubmitOnTabChange = false,
  isLoading = false,
  multiSelectKeys = NO_MULTI_SELECT_KEYS,
  onFiltersApplied,
}: ReportFiltersProps) {
  // Products masking (real drug names -> "Product 1", "Product 2", …) is
  // only for demo accounts — everyone else sees real product names.
  const { user: userData } = useAuthStore();
  const isDemoUser = isDemoUserEmail(userData?.email);

  const tabFields = inputFields.filter(
    (field) => field.control_type === "tabs",
  );
  const nonTabFields = inputFields.filter(
    (field) => field.control_type !== "tabs",
  );

  const isMultiKey = (key: string) => multiSelectKeys.includes(key);

  const [formValues, setFormValues] = useState<FilterValues>(() =>
    normalizeMultiSelectFilters(defaultValues, multiSelectKeys),
  );
  const [dateValues, setDateValues] = useState<Record<string, Date | null>>({});
  const [searchSelections, setSearchSelections] = useState<
    Record<string, SelectOption | null>
  >({});
  const [filterError, setFilterError] = useState<string | null>(null);
  const startDateRef = useRef<ReactDatePicker>(null);
  const endDateRef = useRef<ReactDatePicker>(null);

  const closeDatePickers = () => {
    startDateRef.current?.setOpen(false);
    endDateRef.current?.setOpen(false);
  };

  const isInsuranceFilterKey = (key: string) => {
    const normalizedKey = key.toLowerCase();
    return (
      normalizedKey.includes("insurance") ||
      normalizedKey.includes("isurance") ||
      normalizedKey.includes("isurnace")
    );
  };

  useEffect(() => {
    const nextInitialValues = getInitialValues(inputFields);
    const searchableFieldKeys = new Set(
      inputFields
        .filter((field) => field.control_type === "search")
        .map((field) => field.key),
    );

    setFormValues((previousValues) => {
      // Normalized on the way in so a multi-select key is an array from the
      // first render: getInitialValues seeds it from `default_value`, which is
      // a scalar, and a value handed down as defaultValues may be one too.
      if (Object.keys(defaultValues).length > 0) {
        return normalizeMultiSelectFilters(
          {
            ...nextInitialValues,
            ...defaultValues,
          },
          multiSelectKeys,
        );
      }

      return normalizeMultiSelectFilters(
        {
          ...nextInitialValues,
          ...previousValues,
        },
        multiSelectKeys,
      );
    });

    setDateValues({
      start_date: toValidDate(
        defaultValues.start_date,
        String(nextInitialValues.start_date ?? START_DATE),
      ),
      end_date: toValidDate(
        defaultValues.end_date,
        String(nextInitialValues.end_date ?? END_DATE),
      ),
    });
    setFilterError(null);
    setSearchSelections((previousSelections) => {
      const nextSelections: Record<string, SelectOption | null> = {};

      Object.keys(previousSelections).forEach((key) => {
        if (searchableFieldKeys.has(key)) {
          const selectedOption = previousSelections[key];
          if (selectedOption !== undefined) {
            nextSelections[key] = selectedOption;
          }
        }
      });

      return nextSelections;
    });
  }, [inputFields, defaultValues, multiSelectKeys]);

  const handleInputChange = (name: string, value: FilterValue) => {
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  // A select whose key is multi keeps its value as an array, so the payload
  // shape is the same whether one product is picked or three, and "nothing
  // picked" is [] rather than "". toMultiFilterValue also drops the
  // "All Products" option value if react-select ever reports one back.
  const handleSelectChange = (key: string, value: FilterValue) => {
    if (isMultiKey(key)) {
      handleInputChange(key, toMultiFilterValue(value));
      return;
    }
    if (!Array.isArray(value)) handleInputChange(key, value);
  };

  const handleTabChange = (name: string, value: string | number) => {
    const nextValues: FilterValues = {
      ...formValues,
      [name]: value,
    };

    setFormValues(nextValues);

    if (autoSubmitOnTabChange) {
      onSubmit?.({ fields: nextValues });
    }
  };

  const handleDateChange = (key: string, value: Date | null) => {
    setDateValues((prev) => ({ ...prev, [key]: value }));
    setFormValues((prev) => {
      const nextValues = {
        ...prev,
        [key]: value ? format(value, "yyyy-MM-dd") : "",
      };

      const nextStart = dateText(nextValues.start_date);
      const nextEnd = dateText(nextValues.end_date);
      if (nextStart && nextEnd) {
        if (new Date(nextStart) > new Date(nextEnd)) {
          setFilterError("End Date cannot be earlier than Start Date.");
        } else {
          setFilterError(null);
        }
      } else {
        setFilterError(null);
      }

      return nextValues;
    });
  };

  const handleSearchChange = (key: string, option: SelectOption | null) => {
    setSearchSelections((prev) => ({ ...prev, [key]: option }));
    setFormValues((prev) => ({
      ...prev,
      [key]:
        option == null
          ? ""
          : isInsuranceFilterKey(key)
            ? String(option.label)
            : String(option.value),
    }));
  };

  const getFilteredData = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (filterError) return;

    const start = dateText(formValues.start_date);
    const end = dateText(formValues.end_date);
    if (start && end) {
      if (new Date(start) > new Date(end)) {
        setFilterError("End Date cannot be earlier than Start Date.");
        return;
      }
    }
    setFilterError(null);
    onSubmit?.({
      fields: formValues,
    });
  };

  const resetFilterData = () => {
    const nextInitialValues = getInitialValues(inputFields);

    setFormValues(
      normalizeMultiSelectFilters(nextInitialValues, multiSelectKeys),
    );
    setDateValues({
      start_date: toValidDate(nextInitialValues.start_date, String(START_DATE)),
      end_date: toValidDate(nextInitialValues.end_date, String(END_DATE)),
    });
    setSearchSelections({});
    setFilterError(null);
    onClear?.();
  };

  return (
      <form id="filter-form" className="flex-fill" onSubmit={getFilteredData}>
          <h5 className="d-flex align-items-center mb-3"><i className="bi bi-filter fs-4"></i> &nbsp;Filters</h5>
          {filterError && (
              <div className="alert alert-danger py-2 mb-3" role="alert">
                  {filterError}
              </div>
          )}
          {tabFields.length > 0 && (
              <div className="mb-3 d-flex flex-column gap-2">
                  {tabFields.map((inputProps) => (
                      <Tabs
                          key={inputProps.key}
                          options={inputProps.static_options ?? []}
                          value={scalarValue(
                              formValues[inputProps.key] ?? defaultValues[inputProps.key],
                          )}
                          onChange={(value) => handleTabChange(inputProps.key, value)}
                      />
                  ))}
              </div>
          )}
          <div id="filter-form-fields" className="px-2">
              {nonTabFields.length > 0 ? (
                  nonTabFields.map((inputProps) => {
                      if (inputProps.control_type === "search") {
                          return inputProps.api_endpoint ? (
                              <div className="mb-3" key={inputProps.key}>
                                  <SelectWithSearch
                                      key={inputProps.key}
                                      endpoint={inputProps.api_endpoint}
                                      value={searchSelections[inputProps.key] ?? null}
                                      onChange={(option) =>
                                          handleSearchChange(inputProps.key, option)
                                      }
                                      placeholder={inputProps.label ?? "Search..."}
                                  />
                              </div>
                          ) : null;
                      }

                      if (
                          inputProps.control_type === "select" &&
                          inputProps.key === "drug_name"
                      ) {
                          const isMultiProducts = isMultiKey(inputProps.key);
                          // "All Products" is a single-select affordance. With
                          // a multi-select, picking nothing already means every
                          // product, so the entry is dropped from the list and
                          // the placeholder says so instead — leaving it in
                          // would put a chip in the box that contradicts the
                          // products beside it.
                          const productOptions = (
                              inputProps.static_options ?? []
                          ).filter(
                              (option) =>
                                  !isMultiProducts ||
                                  !isAllFilterOptionValue(option.value),
                          );
                          // Real drug names shouldn't show on screen for demo
                          // accounts — the label becomes "Product N" while
                          // `value` (what's actually submitted) stays the real
                          // name. Shared with DataContextBar, which prints the
                          // same selection back as a sentence and has to use
                          // the same numbering. Empty for everyone else, so
                          // getOptionLabel below falls through to the real label.
                          const maskedLabelByValue = maskedProductLabels(
                              productOptions,
                              isDemoUser,
                          );

                          return (
                              <div className="mb-3" key={inputProps.key}>
                                  {showLabels && (
                                      <label className="form-label">Products</label>
                                  )}
                                  <Select
                                      key={inputProps.key}
                                      id={inputProps.key}
                                      name={inputProps.key}
                                      formValues={formValues}
                                      defaultValues={defaultValues}
                                      api_endpoint={inputProps.api_endpoint}
                                      onChange={(value) =>
                                          handleSelectChange(inputProps.key, value)
                                      }
                                      onMenuOpen={closeDatePickers}
                                      isMulti={isMultiProducts}
                                      // Single select: an unset Products filter means
                                      // "all products" — shown as a real selected value
                                      // (emptyLabel), not react-select's greyed-out
                                      // placeholder, so it reads as the current
                                      // selection rather than a prompt to pick
                                      // something. A multi-select can't do that (see
                                      // Select.calculateSelectValue), so it says the
                                      // same thing in the placeholder.
                                      emptyLabel={
                                          isMultiProducts ? undefined : "All Products"
                                      }
                                      placeholder={
                                          isMultiProducts ? "All Products" : undefined
                                      }
                                      static_options={productOptions}
                                      // Product names are longer than the
                                      // 230px panel, so a selected one is
                                      // always clipped — hover shows it whole.
                                      showValueTooltip
                                      getOptionLabel={(option) =>
                                          maskedLabelByValue.get(option.value) ??
                                          option.label
                                      }
                                  />
                              </div>
                          );
                      }

                      if (
                          inputProps.control_type === "select" &&
                          inputProps.key === "period"
                      ) {
                          return (
                              <div className="mb-3" key={inputProps.key}>
                                  {showLabels && (
                                      <label className="form-label">Group By</label>
                                  )}
                                  <Select
                                      key={inputProps.key}
                                      id={inputProps.key}
                                      name={inputProps.key}
                                      formValues={formValues}
                                      defaultValues={defaultValues}
                                      api_endpoint={inputProps.api_endpoint}
                                      onChange={(value) =>
                                          handleSelectChange(inputProps.key, value)
                                      }
                                      onMenuOpen={closeDatePickers}
                                      placeholder={inputProps.label ?? "inputProps.key"}
                                      static_options={
                                          inputProps.static_options ? inputProps.static_options : []
                                      }
                                  />
                              </div>
                          );
                      }

                      if (
                          inputProps.control_type === "date" &&
                          inputProps.key === "start_date"
                      ) {
                          return (
                              <div className="mb-3" key={inputProps.key}>
                                  {showLabels && (
                                      <label className="form-label">Start Date</label>
                                  )}
                                  <DatePicker
                                      ref={startDateRef}
                                      key={inputProps.key}
                                      id={inputProps.key}
                                      name={inputProps.key}
                                      value={dateValues[inputProps.key] ?? null}
                                      onChange={(value) => handleDateChange(inputProps.key, value)}
                                      placeholder={inputProps.placeholder ?? "MM/dd/yyyy"}
                                  />
                              </div>
                          );
                      }
                      if (
                          inputProps.control_type === "date" &&
                          inputProps.key === "end_date"
                      ) {
                          return (
                              <div className="mb-3" key={inputProps.key}>
                                  {showLabels && (
                                      <label className="form-label">End Date</label>
                                  )}
                                  <DatePicker
                                      ref={endDateRef}
                                      key={inputProps.key}
                                      id={inputProps.key}
                                      name={inputProps.key}
                                      value={dateValues[inputProps.key] ?? null}
                                      onChange={(value) =>
                                          handleDateChange(inputProps.key, value)
                                      }
                                      placeholder={inputProps.placeholder ?? "MM/dd/yyyy"}
                                  />
                              </div>
                          );
                      }

                      return (
                          <div className={inputProps.control_type === "hidden" ? "d-none" : "col filter-col"} key={inputProps.key}>
                              <Input
                                  key={inputProps.key}
                                  id={inputProps.key}
                                  name={inputProps.key}
                                  type={inputProps.control_type}
                                  placeholder={inputProps.label ?? undefined}
                                  value={scalarValue(formValues[inputProps.key]) ?? ""}
                                  onChange={(value) => handleInputChange(inputProps.key, value)}
                              />
                          </div>
                      );
                  })
              ) : inputFields.length === 0 ? (
                  <div className="placeholder-glow col-12 col-sm-12 col-md-9 col-lg-10">
                      <span className="placeholder w-100" style={{height: "38px"}}></span>
                  </div>
              ) : null}

              <div className="row g-3 my-3">
                  <div className="col">
                      <button type="submit" className="btn btn-primary w-100" onClick={onFiltersApplied}>
                          <i className="bi bi-filter"></i> Filter
                      </button>
                  </div>
                  <div className="col">
                      <button type="button" onClick={resetFilterData} className="btn btn-light w-100">
                          <i className="bi bi-arrow-clockwise"></i> Clear
                      </button>
                  </div>
              </div>
          </div>
      </form>
  );
}
